import {
  carriedEntries,
  BAG_SIZE,
  type InventoryState,
  isTradable,
  TRADE_MAX_ITEMS,
  tradeBlock,
  TradeEventSchema,
  TradeRejectedSchema,
  TradeRequestedSchema,
  TradeStateSchema,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import type { InventoryPanel } from './inventory-panel.js';
import { InvitePrompt } from './invite-prompt.js';
import { TradePanel } from './trade-panel.js';
import { uiFormat } from './text.js';
import { TRADE_REFUSAL_TEXT, tradeEventText } from './trade-text.js';

/** The little of a Colyseus room this needs. */
interface TradeRoom {
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

export interface TradeClientDeps {
  chat: { notice: (text: string) => void };
  /** The local inventory, read when needed: it arrives and changes after the join. */
  inventory: () => InventoryState | undefined;
  /** The inventory window, which opens in pick mode for the trade's duration. */
  inventoryPanel: () => InventoryPanel | undefined;
  icons: () => CssAtlas | null;
}

interface OfferedStack {
  itemId: string;
  quantity: number;
}

const EMPTY_BAG: InventoryState = {
  bagSize: BAG_SIZE,
  bag: [],
  wardrobe: [],
  equipped: {},
  gold: 0,
};

/**
 * The client's side of a trade (ADR 0034): shows what the server says — the
 * question, the two offers, what happened — and turns clicks into
 * intentions. It keeps only my current offer, to compute the next one from
 * it when a stack joins from the bag: the server replaces offers whole.
 */
export class TradeClient {
  /** Built on first use: the icon atlas lands after the room is joined. */
  private panel: TradePanel | undefined;
  private readonly prompt: InvitePrompt;
  private offer: OfferedStack[] = [];
  private gold = 0;
  private trading = false;
  /** The inventory window holding the pick mode: rebuilt when the icons land, so re-armed. */
  private armedPanel: InventoryPanel | undefined;

  constructor(
    private readonly room: TradeRoom,
    private readonly deps: TradeClientDeps,
  ) {
    this.prompt = new InvitePrompt((from, accept) => {
      room.send('trade_answer', { from, accept });
    });

    room.onMessage('trade', (payload) => {
      const parsed = TradeStateSchema.safeParse(payload);
      if (!parsed.success) return;
      const state = parsed.data;
      if (state === null) {
        this.endTrade();
        return;
      }
      // The server's view of my offer is the one to build on.
      this.offer = state.mine.items.map(({ itemId, quantity }) => ({ itemId, quantity }));
      this.gold = state.mine.gold;
      this.panelOf().show(state, deps.inventory() ?? EMPTY_BAG);
      if (!this.trading || this.armedPanel !== deps.inventoryPanel()) this.beginTrade();
    });
    room.onMessage('trade_requested', (payload) => {
      const parsed = TradeRequestedSchema.safeParse(payload);
      if (!parsed.success) return;
      this.prompt.show({
        from: parsed.data.from,
        text: uiFormat('{name} veut échanger avec toi.', { name: parsed.data.name }),
        expiresInMs: parsed.data.expiresInMs,
      });
    });
    room.onMessage('trade_event', (payload) => {
      const parsed = TradeEventSchema.safeParse(payload);
      if (parsed.success) deps.chat.notice(tradeEventText(parsed.data));
    });
    room.onMessage('trade_rejected', (payload) => {
      const parsed = TradeRejectedSchema.safeParse(payload);
      if (!parsed.success) return;
      const text = TRADE_REFUSAL_TEXT[parsed.data.reason];
      deps.chat.notice(text);
      if (this.panel?.isOpen === true) this.panel.say(text);
    });
  }

  private panelOf(): TradePanel {
    this.panel ??= new TradePanel({
      icons: this.deps.icons(),
      onOffer: (items, gold) => {
        this.sendOffer(items, gold);
      },
      onConfirm: () => {
        this.room.send('trade_confirm', {});
      },
      onCancel: () => {
        this.room.send('trade_cancel', {});
        this.endTrade();
      },
    });
    return this.panel;
  }

  /** Propose a trade to the player of that name (the menu, or `/echanger nom`). */
  request(name: string): void {
    this.room.send('trade_request', { name });
  }

  /** The trade is on: the bag opens beside the window, every unbound item a click away. */
  private beginTrade(): void {
    this.trading = true;
    const inventoryPanel = this.deps.inventoryPanel();
    this.armedPanel = inventoryPanel;
    inventoryPanel?.setPickMode({
      label: 'Échanger',
      canPick: (instance) => isTradable(instance),
      onPick: (itemId, quantity) => {
        this.addToOffer(itemId, quantity);
      },
    });
    inventoryPanel?.toggle(true);
  }

  /** Over, whatever the reason: the window closes and the bag is a bag again. */
  private endTrade(): void {
    this.trading = false;
    this.offer = [];
    this.gold = 0;
    this.panel?.close();
    this.armedPanel?.setPickMode(null);
    this.armedPanel = undefined;
  }

  /**
   * A bag item joins my offer: merged into its stack if already there,
   * never more of it than the bag holds, never more stacks than the table
   * takes. The whole offer goes out; the server answers with the new state.
   */
  private addToOffer(itemId: string, quantity: number): void {
    if (!this.trading) return;
    const inventory = this.deps.inventory();
    const entry =
      inventory === undefined
        ? undefined
        : carriedEntries(inventory).find((candidate) => candidate.item.id === itemId);
    if (entry === undefined) return;
    const blocked = tradeBlock(entry.item);
    if (blocked !== null) {
      this.panel?.say(TRADE_REFUSAL_TEXT[blocked]);
      return;
    }
    const existing = this.offer.find((stack) => stack.itemId === itemId);
    if (existing === undefined && this.offer.length >= TRADE_MAX_ITEMS) {
      this.panel?.say(uiFormat('{max} piles au plus sur la table.', { max: TRADE_MAX_ITEMS }));
      return;
    }
    const wanted = Math.min((existing?.quantity ?? 0) + quantity, entry.item.quantity);
    if (existing?.quantity === wanted) return;
    const items =
      existing === undefined
        ? [...this.offer, { itemId, quantity: wanted }]
        : this.offer.map((stack) =>
            stack.itemId === itemId ? { itemId, quantity: wanted } : stack,
          );
    this.sendOffer(items, this.gold);
  }

  private sendOffer(items: OfferedStack[], gold: number): void {
    if (!this.trading) return;
    this.offer = items.map(({ itemId, quantity }) => ({ itemId, quantity }));
    this.gold = gold;
    this.room.send('trade_offer', { items: this.offer, gold });
  }

  destroy(): void {
    this.prompt.close();
    this.panel?.destroy();
    this.panel = undefined;
    this.armedPanel?.setPickMode(null);
    this.armedPanel = undefined;
  }
}
